import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo } from "react";
import { useLoginStatus } from "../../../hooks/useLoginStatus";
import useTranslation from "../../../hooks/useTranslation";
import {
  EVENT_LABEL_BY_TYPE,
  eventRows,
  formatCurrency,
  formatDateTime,
  formatNumber,
  staffUsers,
} from "../../../lib/staff_console.shared";

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <tr className="border-b border-slate-200 last:border-b-0">
      <th className="px-0 py-2 text-left text-xs font-medium normal-case tracking-[0.08em] text-slate-600">
        {label}
      </th>
      <td className="px-0 py-2 text-right text-sm font-light tabular-nums text-slate-950">
        {value}
      </td>
    </tr>
  );
}

function SpaceListRow({
  label,
  values,
}: {
  label: string;
  values: string[];
}) {
  return (
    <tr className="border-b border-slate-200 last:border-b-0">
      <th className="px-0 py-2 text-left text-xs font-medium normal-case tracking-[0.08em] text-slate-600">
        {label}
      </th>
      <td className="px-0 py-2 text-right text-sm font-light tabular-nums text-slate-950">
        <span className="inline-flex flex-wrap justify-end gap-x-2 gap-y-1">
          {values.map((value) => (
            <span key={value} className="text-slate-950">
              {value}
            </span>
          ))}
        </span>
      </td>
    </tr>
  );
}

function formatDuration(seconds: number) {
  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes} min`;
  }

  return `${hours} h ${minutes} min`;
}

export const Route = createFileRoute("/app/staff_console_user/$userId")({
  component: StaffConsoleUserPage,
});

function StaffConsoleUserPage() {
  const loginStatus = useLoginStatus();
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const { userId } = Route.useParams();
  const isStaff =
    loginStatus.data?.state === "logged in" && loginStatus.data.is_staff;

  useEffect(() => {
    if (loginStatus.isSuccess && !isStaff) {
      navigate({ to: "/app/product_logbook", replace: true });
    }
  }, [isStaff, loginStatus.isSuccess, navigate]);

  const selectedUser = useMemo(
    () => staffUsers.find((user) => user.id === userId),
    [userId],
  );

  const selectedUserEvents = useMemo(
    () => eventRows.filter((row) => row.userId === userId),
    [userId],
  );

  const voiceAgentEvents = useMemo(
    () => selectedUserEvents.filter((row) => row.type === "speaking turn"),
    [selectedUserEvents],
  );

  const voiceAgentTotals = useMemo(
    () =>
      voiceAgentEvents.reduce(
        (totals, row) => ({
          tokens: totals.tokens + row.tokens,
          durationSeconds: totals.durationSeconds + (row.durationSeconds ?? 0),
        }),
        { tokens: 0, durationSeconds: 0 },
      ),
    [voiceAgentEvents],
  );

  if (!loginStatus.isSuccess || !isStaff) {
    return <div className="w-full px-4 py-6 sm:px-8 lg:px-12" />;
  }

  if (!selectedUser) {
    return (
      <div className="w-full px-4 py-6 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-4xl space-y-6">
          <Link
            to="/app/staff_console"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-700"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {translate("staff_console_back_to_users")}
          </Link>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
            {translate("staff_console_user_detail_title")}
          </h1>
          <p className="text-sm text-slate-700">
            {translate("staff_console_user_detail_not_found")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-4xl space-y-8">
        <Link
          to="/app/staff_console"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {translate("staff_console_back_to_users")}
        </Link>

        <header className="space-y-2">
          <h1 className="text-4xl font-light tracking-tight text-slate-950 sm:text-5xl">
            {selectedUser.name}
          </h1>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-light text-slate-950">{selectedUser.name}</span>
            <span className="font-light text-slate-500">{selectedUser.email}</span>
            {selectedUser.isTestGenerated ? (
              <span className="border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700">
                {translate("staff_console_test_generated")}
              </span>
            ) : null}
          </div>
        </header>

        <section className="space-y-6">
          <table className="w-full text-sm">
            <tbody>
              <MetricRow label={translate("staff_console_user_detail_active")} value={selectedUser.activeToday ? "yes" : "no"} />
              <MetricRow label={translate("staff_console_user_detail_last_seen")} value={formatDateTime(selectedUser.lastActiveAt)} />
              <MetricRow label={translate("staff_console_user_detail_lifetime_tokens")} value={formatNumber(selectedUser.tokens7d)} />
              <MetricRow label={translate("staff_console_user_detail_tokens_this_month")} value={formatNumber(selectedUser.tokensThisMonth)} />
              <MetricRow label={translate("staff_console_user_detail_lifetime_spend")} value={formatCurrency(selectedUser.spend7dUsd)} />
              <MetricRow label={translate("staff_console_user_detail_sessions")} value={formatNumber(selectedUser.sessions7d)} />
              <MetricRow label={translate("staff_console_user_detail_voice_agent_time")} value={formatDuration(voiceAgentTotals.durationSeconds)} />
              <MetricRow label={translate("staff_console_user_detail_voice_agent_tokens")} value={formatNumber(voiceAgentTotals.tokens)} />
              <SpaceListRow
                label={translate("staff_console_user_detail_active_spaces")}
                values={selectedUser.activeLanguageSpaces}
              />
              <MetricRow label={translate("staff_console_user_detail_primary_space")} value={selectedUser.languageSpace} />
              <MetricRow label={translate("staff_console_user_detail_generated_texts")} value={formatNumber(selectedUser.generatedTexts7d)} />
              <MetricRow label={translate("staff_console_user_detail_vocabulary_items")} value={formatNumber(selectedUser.vocabularyItems7d)} />
            </tbody>
          </table>

          <div className="space-y-4">
            <div className="flex items-end justify-between gap-4">
              <p className="text-xs font-medium normal-case tracking-[0.08em] text-slate-600">
                {translate("staff_console_user_detail_timeline")}
              </p>
              <p className="text-xs font-medium normal-case tracking-[0.08em] text-slate-600">
                {translate("staff_console_col_tokens")}
              </p>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {selectedUserEvents.map((row) => (
                  <tr key={`${row.at}-${row.type}`} className="border-b border-slate-200 last:border-b-0">
                    <td className="px-0 py-3 font-light text-slate-950">
                      {formatDateTime(row.at)} {translate(EVENT_LABEL_BY_TYPE[row.type] ?? "")}
                    </td>
                    <td className="px-0 py-3 text-right font-light tabular-nums text-slate-700">
                      {formatNumber(row.tokens)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
